/**
 * ========================
 * PROCEDURES DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu configuration for the Procedures Management console (step-by-step operating procedures).
 * Sections:
 *  1. Overview: Dashboard
 *  2. Procedures: All procedures, Import with AI, QR sheet
 *  3. Activity: Runs, Problems
 *  4. Administration: Categories, Settings
 *
 * Visibility comes from PROCEDURES_PAGE_ACCESS in useProceduresPermissions, the same predicates the
 * routes hand to ProceduresGate, so an item shows exactly when its page would open. A section shows
 * when any of its items does.
 */

import {
  IconDashboard,
  IconListDetails,
  IconFileImport,
  IconQrcode,
  IconPlayerPlay,
  IconFlag,
  IconCategory,
  IconSettings,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection } from '../../layouts/menu/types';
import { PROCEDURES_PAGE_ACCESS as page, proceduresMenuVisibility as when } from '../../hooks/procedures/useProceduresPermissions';

class ProceduresMenuProvider implements DomainMenuProvider {
  domainId = 'procedures';

  getMenuConfig() {
    const sections: MenuSection[] = [
      {
        id: 'procedures-overview',
        label: 'Overview',
        visible: when(page.dashboard),
        items: [{ id: 'procedures-dashboard', label: 'Dashboard', path: '/app/procedures', icon: <IconDashboard size={16} />, visible: when(page.dashboard) }],
      },
      {
        id: 'procedures-procedures',
        label: 'Procedures',
        visible: when(page.list, page.import, page.qrSheet),
        items: [
          { id: 'procedures-list', label: 'All procedures', path: '/app/procedures/list', icon: <IconListDetails size={16} />, visible: when(page.list) },
          { id: 'procedures-import', label: 'Import with AI', path: '/app/procedures/import', icon: <IconFileImport size={16} />, visible: when(page.import) },
          { id: 'procedures-qr-sheet', label: 'QR sheet', path: '/app/procedures/qr-sheet', icon: <IconQrcode size={16} />, visible: when(page.qrSheet) },
        ],
      },
      {
        id: 'procedures-activity',
        label: 'Activity',
        visible: when(page.runs, page.problems),
        items: [
          { id: 'procedures-runs', label: 'Runs', path: '/app/procedures/runs', icon: <IconPlayerPlay size={16} />, visible: when(page.runs) },
          { id: 'procedures-problems', label: 'Problems', path: '/app/procedures/problems', icon: <IconFlag size={16} />, visible: when(page.problems) },
        ],
      },
      {
        id: 'procedures-administration',
        label: 'Administration',
        visible: when(page.categories, page.settings),
        items: [
          { id: 'procedures-categories', label: 'Categories', path: '/app/procedures/categories', icon: <IconCategory size={16} />, visible: when(page.categories) },
          { id: 'procedures-settings', label: 'Settings', path: '/app/procedures/settings', icon: <IconSettings size={16} />, visible: when(page.settings) },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '1.0.0',
        lastUpdated: Date.now(),
        description: 'Procedures management menu',
      },
    };
  }
}

export const proceduresMenuProvider = new ProceduresMenuProvider();
